import type { z } from 'zod';

/** What travels along an edge (the chips in the Figma legend). */
export const DATA_TYPES = [
  'source',
  'blueprint',
  'script',
  'clip',
  'reel',
  'carousel',
  'image',
] as const;
export type DataType = (typeof DATA_TYPES)[number];

export const DATA_TYPE_LABELS: Record<DataType, string> = {
  source: 'Source',
  blueprint: 'Blueprint',
  script: 'Script',
  clip: 'Clip',
  reel: 'Reel',
  carousel: 'Carousel',
  image: 'Image',
};

/** Library groups, in the order the node library shows them. */
export const CATEGORIES = [
  'starter',
  'filter',
  'generate',
  'edit',
  'experiment',
  'guardrail',
  'output',
] as const;
export type Category = (typeof CATEGORIES)[number];

export const CATEGORY_LABELS: Record<Category, string> = {
  starter: 'Starters',
  filter: 'Filter & analyse',
  generate: 'Generate',
  edit: 'Edit',
  experiment: 'Experiment',
  guardrail: 'Guardrails & control',
  output: 'Outputs',
};

/** Which worker queue runs a node: control is cheap and in-process, the rest call vendors. */
export type Executor = 'control' | 'llm' | 'render' | 'publish';

export const PLATFORMS = ['TIKTOK', 'INSTAGRAM', 'YOUTUBE', 'LINKEDIN'] as const;

export interface InputPort {
  id: string;
  label?: string;
  /**
   * The data types this port takes; `any` passes whatever arrives straight
   * through; `trigger` is a starter's control handle (no items).
   */
  accepts: readonly DataType[] | 'any' | 'trigger';
  /** `required`: only variant sets (Pick winner). Otherwise either. */
  variant?: 'required';
  /** Can stay unconnected. Trigger handles always can. */
  optional?: boolean;
}

export interface OutputPort {
  id: string;
  label?: string;
  /** A fixed type, `same` as what came in, or a trigger (control edge). */
  type: DataType | 'same' | 'trigger';
  /** Default: a variant set when any input is one. `set` / `clear` override it. */
  variant?: 'set' | 'clear';
}

/** Item counts keyed by port id. */
export type PortCounts = Record<string, number>;

export interface NodeDefinition<C extends z.ZodType = z.ZodType> {
  /** Stable key stored in graphs, e.g. `generate.hookVariations`. */
  type: string;
  /** Bumped when the config or ports change incompatibly; graphs keep theirs. */
  version: number;
  category: Category;
  label: string;
  description: string;
  /** Phosphor icon name. */
  icon: string;
  status: 'ga' | 'soon';
  inputs: readonly InputPort[];
  outputs: readonly OutputPort[];
  /** `map`: one task per incoming item. `collect`: one task once every input is settled. */
  mode: 'map' | 'collect';
  executor: Executor;
  config: C;
  /**
   * Items each output emits, given how many arrived per input port. An upper
   * bound when `bounded` is set (filters, reviews, data-dependent starters).
   */
  cardinality(config: z.output<C>, inputs: PortCounts): PortCounts;
  /** The count depends on data, so estimates show "~". */
  bounded?: boolean;
  /** Renders charged per emitted item (video and audio output). */
  rendersPerItem?: number;
}
